import { useRef, useState } from "react";
import { Upload, Trash2, Star, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { uploadProductImage, uploadUrl } from "@/lib/api";

export default function ImagesUploader({ images, onChange }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const next = [...images];
    for (const f of files) {
      try {
        const { url } = await uploadProductImage(f);
        next.push(url);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Couldn't upload ${f.name}`);
      }
    }
    onChange(next);
    setBusy(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const setAsCover = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [picked] = next.splice(idx, 1);
    next.unshift(picked);
    onChange(next);
  };

  const reorderTo = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...images];
    const [picked] = next.splice(from, 1);
    next.splice(to, 0, picked);
    onChange(next);
  };

  return (
    <div>
      <div
        data-testid="product-images-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-2 py-8 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"
        }`}
      >
        <Upload size={20} strokeWidth={1.5} className="text-muted-foreground" />
        <p className="text-sm text-foreground">{busy ? "Uploading…" : "Drag images here or click to upload"}</p>
        <p className="text-[11px] text-muted-foreground">JPG, PNG, WEBP up to 8MB each</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          data-testid="product-images-input"
        />
      </div>

      {images.length > 0 && (
        <>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Drag a tile to reorder. First image is the cover shown to buyers.
          </p>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {images.map((src, idx) => {
              const isCover = idx === 0;
              const isDragTarget = overIdx === idx && dragIdx !== idx;
              return (
                <div
                  key={`${src}-${idx}`}
                  data-testid={`product-image-tile-${idx}`}
                  draggable
                  onDragStart={(e) => {
                    setDragIdx(idx);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (overIdx !== idx) setOverIdx(idx);
                  }}
                  onDragLeave={() => setOverIdx((v) => (v === idx ? null : v))}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    reorderTo(dragIdx, idx);
                    setDragIdx(null);
                    setOverIdx(null);
                  }}
                  onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                  className={`relative group rounded-lg overflow-hidden border bg-secondary cursor-grab active:cursor-grabbing transition-shadow ${
                    isDragTarget ? "border-primary ring-2 ring-primary/40" : "border-border"
                  } ${dragIdx === idx ? "opacity-50" : ""}`}
                >
                  <img src={uploadUrl(src)} alt="" className="w-full h-32 object-cover pointer-events-none" />
                  <div className="absolute top-1 left-1 p-1 rounded bg-black/50 text-white opacity-70 group-hover:opacity-100">
                    <GripVertical size={12} strokeWidth={1.5} />
                  </div>
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!isCover && (
                      <button
                        type="button"
                        onClick={() => setAsCover(idx)}
                        data-testid={`product-image-set-cover-${idx}`}
                        className="p-1.5 rounded-full bg-black/60 text-white hover:bg-primary"
                        aria-label="Set as cover"
                        title="Set as cover"
                      >
                        <Star size={12} strokeWidth={1.5} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onChange(images.filter((_, i) => i !== idx))}
                      data-testid={`product-image-remove-${idx}`}
                      className="p-1.5 rounded-full bg-black/60 text-white hover:bg-destructive"
                      aria-label="Remove image"
                      title="Remove"
                    >
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                  <p className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-white text-[10px] tracking-wide ${
                    isCover ? "bg-primary" : "bg-black/50"
                  }`}>
                    {isCover ? "Cover" : `#${idx + 1}`}
                  </p>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
